import { clone } from "lodash";
import { notifyError, getEnv, getWindow } from "../../effects-bridge";
import { LOCALSTORAGE_KEYS, QWIKCILVER } from "../../utils/constants";
import type { LoginTypes } from "../../data/constants";
import type { SaleDataType } from "../../models/sale";
import { getLocalStorage } from "../../utils/localStorage";
import { isBetaOrProdEnv } from "../../data/envConstants";
import store from "../../redux-store";
import { removeAcceptedOrder, removeOrderFromNotifications } from "../../redux-store/slices/NotificationsSlice";
import history from "../../utils/navigation";
import { LICENSE_LANDING_ROUTE } from "../../commonComponents/LeftMenu/menuItemsData";
import { performLogout } from "../../facade/ServiceFacade";
import { saveSaleData } from "../../redux-store/slices/SaleSlice";
export * from "./shared";
// `export *` makes no local binding, and both gates below call it
import { isSubwayAccount } from "./shared";

/**
 * Is this the web build? Release's `AddNewTable` (2026-10-05 merge) asks it. Release wrote it in
 * `./shared.ts` as `Platform.OS === "web"`; it is a per-platform constant, so each variant answers
 * it and `./shared.ts` keeps no `react-native` import. This variant: web and Electron (react-native-web reports `Platform.OS === "web"` on both).
 */
export const isWeb = () => true;

/**
 * WEB variant of `helper/commonHelpers`. See `./shared.ts` for what this folder is and what
 * deliberately stays in the apps.
 */

/**
 * ⚠️ Same NAME as a function in `apps/mobile/src/helper/commonHelpers.ts`, but a DIFFERENT
 * function — do not unify them. Web takes `branchId` as a parameter; mobile takes only `user`
 * and reads `businessData.branchId` off the redux `store` singleton (and returns `true` when
 * there is no business data). Only web's is store-free, so only web's could be uplifted.
 *
 * Its single caller is `createBranchPermittedUsersMap` below, but it is exported in the app
 * so the export surface is preserved here too.
 */
export function isPermittedBranchForUser(user: any, branchId: string) {
  if (user?.permittedBranches?.length) {
    return user?.permittedBranches?.includes(branchId);
  }
  return true;
}

/**
 * Builds `businessUserId -> user` for the users permitted on `branchId`, stamping a
 * `fullName` onto each. Used by web `TableViewSlice.getUsersSuccess`.
 *
 * ⚠️ Mobile's near-twin is `handleSaleUserMap` in `helpers/catalogueHelpers/index.native.ts`
 * — a different source FILE, so it mirrors to a different core folder, and its permission
 * filter is commented out upstream. Kept separate on purpose.
 *
 * `structuredClone` is declared locally because `packages/core`'s tsconfig has no `DOM` lib
 * (it must stay RN-safe); the global exists at runtime in every web/electron target. The
 * clone is what lets the `fullName` mutation below not touch the caller's objects.
 */
declare function structuredClone<T>(value: T): T;

export function createBranchPermittedUsersMap(allUsersList: any[], branchId: string) {
  const branchPermittedUsers = allUsersList?.length
    ? allUsersList.filter((user: any) => isPermittedBranchForUser(user, branchId))
    : [];
  const filteredUsersList = structuredClone(branchPermittedUsers),
    usersMap: Record<string, any> = {};
  filteredUsersList.map(function (obj: any) {
    obj.fullName = obj.name.firstName || "";
    usersMap[obj.businessUserId] = obj;
  });
  return usersMap;
}

/**
 * Strip `errors` from a sale and its items, and (unless told not to) save the cleaned copy.
 *
 * ⚠️ **Divergent only in HOW IT CLONES** — this platform uses `structuredClone`, the other uses
 * its own `deepClone`. Everything else is identical, which is why the body appears in both
 * variants rather than in `shared.ts`: `shared.ts` cannot pick a clone implementation.
 *
 * ⚠️ Note the inverted flag: it dispatches when `shouldSave` is **false**. That reads backwards
 * and is the apps' own; preserved rather than renamed.
 */
export const deleteErrorsField: any = (saleData: any, shouldSave: boolean = false) => {
  let data = structuredClone(saleData);
  if (data?.errors) {
    delete data.errors;
  }
  if (data.saleItems?.length) {
    data.saleItems.map((listItem: any) => {
      if (listItem?.errors) delete listItem?.errors;
    });
  }
  if (!shouldSave) {
    store.dispatch(saveSaleData(data));
  }
  return data;
};

/**
 * Send the user to the landing page their licence entitles them to, after login or a licence
 * change. Called by `redux-store/saga/business`'s `getUserData` when `payload.isRedirect`.
 *
 * ⚠️ **This is the function the NAVIGATION seam was opened for**, and it needed no bridge entry:
 * `history` is now core's own singleton (`utils/navigation/index.desktop`), so this is a plain
 * platform-split uplift. See `utils/navigation/shared.ts` for why that was possible.
 *
 * ⚠️ Mobile's twin is a genuinely different function, not a formatting difference — it branches
 * on the destination and dispatches two differently-shaped react-navigation payloads. That is why
 * there is no `shared.ts` body for it.
 *
 * ⚠️ An unmapped licence code yields `undefined`, and `history.push(undefined)` navigates to the
 * current URL rather than throwing. Preserved — the app has always behaved this way.
 */
export const redirectToLicenseLandingPage = (userAssignedLicense: string) => {
  history.push(LICENSE_LANDING_ROUTE[userAssignedLicense]);
};

// `isSubwayAccount` and `shouldCalculateSale` moved to `./shared.ts` on 2026-09-29 (plan P4): My
// Orders v2 recalculates before checkout on both platforms. `export * from "./shared"` above keeps
// exporting them from here; `preventCloseSaleEnable` imports the one it calls.

/** Module-private in the app file too; the exported gate is `preventCloseSale` below. */
const preventCloseSaleEnable = () => isSubwayAccount() || !isBetaOrProdEnv;

/**
 * Block closing a sale whose balance is negative, and toast why.
 *
 * ⚠️ **WEB-ONLY — there is no mobile twin.** Verified with `grep` across `apps/mobile/src`, not
 * assumed.
 *
 * 🔴 **`request` is BUILT AND NEVER USED.** Twenty lines clone the sale, strip nine fields and
 * reshape `saleItems` into a local that is then discarded — the function returns `preventClose`
 * and nothing reads `request`. It looks like the payload for an audit/telemetry call that was
 * removed. **Preserved verbatim and NOT deleted:** standing rule 2 only removes dead code that
 * is the sole thing making two platforms differ, and this symbol has no other platform. It is a
 * cleanup ticket, flagged here rather than taken.
 *
 * ⚠️ The toast is the injected `notifyError` — core cannot import UI. Both apps wire it to their
 * own toast in `bootstrapCoreBridges.ts`, so this is a symmetric mapping, not a behaviour change.
 */
export function preventCloseSale(saleData: any, params?: any) {
  const preventClose = preventCloseSaleEnable() && saleData?.paymentInfo?.balanceAmount <= -1;
  if (preventClose) {
    const request: any = clone(saleData);
    delete request.billDiscountList;
    delete request.billTaxList;
    delete request.chargeList;
    delete request.deliveryInfo;
    delete request.discountList;
    delete request.itemDiscountList;
    delete request.serviceLabel;
    delete request.tickets;
    request.saleItems = request.saleItems?.map((item: any) => {
      const newItem = {
        itemId: item.itemId,
        categoryName: item.categoryName,
        channelName: item.channelName,
        longName: item.longName,
        skuCode: item.skuCode,
        shortName: item.shortName,
      }
      return newItem;
    });
    notifyError('Cannot close sale with negative balance amount');
  }
  return preventClose;
}

/**
 * Drop one order from the accepted-orders list.
 *
 * ⚠️ **WEB-ONLY — `clearAcceptedOrder` appears nowhere in `apps/mobile`** (repo-wide grep, not
 * assumed). Both slice actions it uses DO exist on both platforms; it is the helper that is
 * one-sided.
 */
export function clearAcceptedOrder(orderId: string) {
  store.dispatch(removeAcceptedOrder({ orderId }));
}

/**
 * Filter one order out of the live notifications list and write the remainder back.
 *
 * ⚠️ WEB-ONLY, same as `clearAcceptedOrder` above.
 *
 * ⚠️ The filter matches on `orderData.event.data.saleVO.saleId`, four levels deep, and any
 * notification whose shape does not reach that path is KEPT (the `?.` chain yields `undefined`,
 * which never equals `orderId`). That is the app's own behaviour and is preserved — it is the
 * safe direction for a filter that removes things.
 */
export function clearAcceptedOrderFromNotifications(orderId: string) {
  let filteredOrders = removeOrderFromArrayBasedOnKey(
    orderId,
    store.getState().notificationsStore?.newOnlineOrders
  );
  store.dispatch(removeOrderFromNotifications({ orders: filteredOrders }));
}

/**
 * Return `orderList` without the entry whose nested `saleVO.saleId` matches.
 *
 * ⚠️ Uplifted alongside its only caller above rather than left behind — it is exported in the app
 * file, so leaving it would have put a second definition beside core's (audit check A would have
 * flagged it, correctly).
 */
export function removeOrderFromArrayBasedOnKey(
  orderId: string,
  orderList: any = []
) {
  let ordersToFilter = [...orderList];
  let filteredOrders = ordersToFilter?.filter((orderData: any) => {
    let originalOrderData = orderData?.event?.data?.saleVO;
    return originalOrderData?.saleId !== orderId;
  });
  return filteredOrders;
}

/**
 * The client metadata sent with a login/register request.
 *
 * ⚠️ **Per-variant, and the two payloads share only `loginType`.** This platform reports the
 * browser user-agent and four build versions; the other reports a device type and one app
 * version. Same function name, same optional argument, entirely different body — a shared
 * version would send fields the backend has never seen from one of the two clients.
 *
 * ⚠️ `nwAppVersion` falls back to the literal `"web"` when there is no native shell. The apps'
 * own behaviour, preserved — the backend distinguishes browser from Electron on that value.
 */
/**
 * The build versions this client reports. **WEB-ONLY.**
 *
 * ⚠️ The three git values come from the effects bridge's `env` bag, not from a JSON import: the
 * app reads `helper/generatedGitInfo.json`, which is REGENERATED at build time by
 * `npm run git-info` and lives inside `apps/web`. Core must not import from an app, and the
 * values are plain build-time data — exactly what the env bag is for.
 *
 * ⚠️ `window.electronAPI.reactVersion` wins over the git value when present: in a packaged
 * Electron shell the OTA-installed React build is the truthful version. Read through
 * `getWindow()` INSIDE the function (hazard 18), never at module level.
 */
export function getCurrentVersions() {
  const oracleVersionInfo = getWindow()?.electronAPI?.reactVersion || getEnv("oracleGitInfo") || "";
  const commonVersionInfo = getEnv("commonGitInfo") || "";
  const ristaPosCommonVersionInfo = getEnv("ristaPosCommonGitInfo") || "";
  const nwAppVersion = getLocalStorage(LOCALSTORAGE_KEYS.nwAppVersion) || "";
  return {
    oracleVersion: oracleVersionInfo,
    commonVersion: commonVersionInfo,
    ristaPosCommonVersion: ristaPosCommonVersionInfo,
    nwAppVersion,
  };
}

/**
 * Which shell this client is running in. **WEB-ONLY.**
 *
 * ⚠️ Returns the EMPTY STRING for any `nwPlatform` other than `win`/`mac` — not `"web"`. That
 * asymmetry is the app's own (an unset value means web; an unrecognised one means unknown) and is
 * preserved verbatim.
 */
export function getCurrentPlatform(): string {
  const nwPlatform = getLocalStorage(LOCALSTORAGE_KEYS.nwPlatform);
  if (!nwPlatform) return "web";
  switch(nwPlatform) {
    case "win":
      return "windows";
    case "mac":
      return "osx";
    default:
      return '';
  }
}

export function getLoginMetaInfo({ loginType }: { loginType?: LoginTypes } = {}) {
  const versionInfo = getCurrentVersions();
  const metaInfo: Record<string, any> = {
    type: getCurrentPlatform(),
    userAgent: navigator.userAgent,
    oracleVersion: versionInfo.oracleVersion,
    commonVersion: versionInfo.commonVersion,
    nwAppVersion: versionInfo.nwAppVersion || "web",
    ristaPosCommonVersion: versionInfo.ristaPosCommonVersion,
  }
  if (loginType) {
    metaInfo.loginType = loginType;
  }
  return metaInfo;
}

/**
 * Reads the stored auth token and ends the session with it.
 *
 * WEB-ONLY: the other platform's `helper/commonHelpers` has no such function (`grep`-verified —
 * the name appears nowhere in that app). Its `AccountControl` screen calls `performLogout`
 * directly instead.
 *
 * ⚠️ **This import closes a CYCLE** — `commonHelpers` → `facade/ServiceFacade` →
 * `ServiceFacade/shared` → `commonHelpers` (for `convertArrayToObject`) — and that is fine, for
 * the reason that decides every cycle in this package: nothing on it READS a binding at
 * module-EVAL time. Both `performLogout` and `convertArrayToObject` are only ever called from
 * inside function bodies. `OfflineHelper` ↔ `ServiceFacade` is the same benign shape.
 *
 * (This note used to contrast with a lazy `require` of `SaleSlice` above. That deferral is GONE as
 * of 2026-08-27: `getBasicSaleData` moved INTO `SaleSlice`, so the slice imports no helper and
 * calls nothing across a module boundary while evaluating. Seven such deferrals were retired
 * together — see the ledger.)
 */
export function triggerLogout() {
  let tokenId = getLocalStorage(LOCALSTORAGE_KEYS.aToken);
  performLogout(tokenId);
}

/**
 * Whether the sale may be saved given its Qwikcilver gift-card payment.
 *
 * False only when a Qwikcilver payment exists AND it exceeds the bill total — an overpaid gift
 * card cannot be settled, so the caller blocks the save. With no Qwikcilver payment on the bill
 * it is vacuously true.
 *
 * WEB-ONLY: the other platform's `helper/commonHelpers` has no Qwikcilver logic at all (grepped
 * for `QWIKCILVER`/`uickCilver` in that file — zero hits), which is why this sits in the variant
 * and not in `shared.ts`. Its `QWIKCILVER` constant, by contrast, IS on both platforms and lives
 * in `utils/constants/shared.ts`.
 */
export const isQuickCilverPaymentAmountValidated = (saleData: SaleDataType) => {
  const qwikcilverPayment = saleData?.paymentInfo?.payments?.find((p: any) => p.mode === QWIKCILVER);
  const billAmount = saleData?.billTotal || 0;
  if (qwikcilverPayment) {
    const quickCilverPaidAmount = qwikcilverPayment?.amount || 0;
    if (quickCilverPaidAmount > billAmount) {
      return false;
    }
  }
  return true;
};

/**
 * Is the terminal rendering at tablet width or narrower?
 *
 * WEB-ONLY: the other platform's `helper/commonHelpers` has no such function — it IS the mobile
 * app.
 *
 * 🔴 **Reads the real `window` global, NOT the effects bridge's `getWindow()` — the second place
 * on the branch where that is correct, for exactly `helpers/envHelper`'s reason (hazard 18).**
 * `apps/web/src/commonComponents/LeftMenu/menuItemsData.ts:393` calls this at MODULE SCOPE
 * (`isShow: !isMobileView()`), and the bridges are wired AFTER core modules evaluate — through
 * `getWindow()` that call threw `TypeError: Cannot read properties of undefined (reading
 * 'innerWidth')` and took app boot down with it.
 *
 * Safe here for envHelper's reason, which does NOT generalise: this file is `.desktop`, so Metro
 * never loads it, and under webpack it always evaluates inside a browser where `window` is a real
 * global. `getCurrentVersions` above still uses `getWindow()` — because it reads window inside a
 * function, at CALL time, where the bridge works. The rule is about WHEN the read happens.
 *
 * ⚠️ 768 is a bare literal in the app file too, not a named breakpoint. Transcribed as-is.
 */
export function isMobileView() {
  return window.innerWidth <= 768;
}

/**
 * Array -> object, keyed by `uniqueKey`. Uplifted from `apps/web/src/helper/commonHelpers`.
 *
 * `.desktop`-only because the source genuinely is: `apps/mobile/src/helper/commonHelpers`
 * has no such function (checked, not assumed). It lives HERE rather than beside its one
 * consumer in `ordering/discounts`, because a core folder mirrors the APP FILE a symbol
 * came from — that is what keeps the core tree a recognisable map of the apps and stops
 * `commonHelpers` shattering across the domains that happen to call into it.
 *
 * ⚠️ NOT the same as `convertArrayToObject`: that one lowercases the key.
 */
export function convertArrayToObjectWithoutLowerCase(inputArr: any, uniqueKey = "mid") {
  return inputArr.reduce((acc: any, cur: any) => ({ ...acc, [cur[uniqueKey]]: cur }), {});
}
